import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Search, Archive, ArchiveRestore, Mail, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMN_SETS = {
  rsvps: [
    { key: "created_at", label: "Data", render: (v) => formatDate(v) },
    { key: "full_name", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Telefone" },
    { key: "company", label: "Empresa" },
    { key: "role", label: "Cargo" },
    { key: "bring_guest", label: "Acompanhante", render: (v) => (v ? "Sim" : "—") },
    { key: "guest_name", label: "Nome acompanhante" },
    { key: "dietary", label: "Restrições" },
    { key: "paid", label: "Pago", render: (v, row) => renderPaid(v, row) },
    { key: "message", label: "Mensagem" },
  ],
  miami: [
    { key: "created_at", label: "Data", render: (v) => formatDate(v) },
    { key: "full_name", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Telefone" },
    { key: "message", label: "Mensagem" },
  ],
  newsletter: [
    { key: "created_at", label: "Data", render: (v) => formatDate(v) },
    { key: "name", label: "Nome", render: (v) => v || "—" },
    { key: "email", label: "Email" },
    { key: "language", label: "Idioma" },
  ],
  contacts: [
    { key: "created_at", label: "Data", render: (v) => formatDate(v) },
    { key: "name", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "subject", label: "Assunto" },
    { key: "message", label: "Mensagem" },
  ],
  donations: [
    { key: "created_at", label: "Data", render: (v) => formatDate(v) },
    { key: "full_name", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Telefone" },
    { key: "company", label: "Empresa" },
    { key: "amount_eur", label: "Valor", render: (v) => v != null ? `${Number(v).toFixed(2)} €` : "—" },
    { key: "paid", label: "Pago", render: (v, row) => renderPaid(v, row) },
    { key: "receipt_number", label: "Declaração", render: (v) => v ? `Nº ${String(v).padStart(4, "0")}` : "—" },
    { key: "billing_nif", label: "NIF" },
    { key: "message", label: "Mensagem" },
  ],
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function renderPaid(paid, row) {
  if (paid) {
    const amount = row?.amount_paid_eur ?? row?.amount_eur;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-emerald-100 text-emerald-800 rounded-sm">
        ✓ Sim{amount != null ? ` · ${Number(amount).toFixed(2)} €` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-amber-50 text-amber-700 rounded-sm border border-amber-200">
      Pendente
    </span>
  );
}

export default function AdminListPage({ title, overline, type, endpoint, csvUrl, archiveBase, defaultEmpty, embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventOptions, setEventOptions] = useState([]); // [{slug, label}]
  const [dynamicTitle, setDynamicTitle] = useState(null);
  const columns = COLUMN_SETS[type];

  // For RSVP pages, allow overriding via ?event=<slug> in URL so admins can
  // browse RSVPs of dynamic events created in /admin/eventos.
  const isRsvpPage = type === "rsvps" || type === "miami";
  const eventOverride = isRsvpPage ? searchParams.get("event") : null;

  const effectiveEndpoint = eventOverride
    ? `/admin/rsvps?event=${encodeURIComponent(eventOverride)}`
    : endpoint;
  const effectiveCsvUrl = eventOverride
    ? `/admin/rsvps.csv?event=${encodeURIComponent(eventOverride)}`
    : csvUrl;
  const effectiveTitle = (eventOverride && dynamicTitle) || title;

  // Load all events so admins can switch between RSVP lists without leaving the page.
  useEffect(() => {
    if (!isRsvpPage) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/events`, { withCredentials: true });
        if (cancelled) return;
        const opts = (r.data?.items || []).map((ev) => ({
          slug: ev.slug,
          label: ev.title_pt || ev.slug,
        }));
        setEventOptions(opts);
        if (eventOverride) {
          const found = opts.find((o) => o.slug === eventOverride);
          setDynamicTitle(found?.label || eventOverride);
        }
      } catch (e) { console.warn("[AdminList] events load failed:", e?.message); }
    })();
    return () => { cancelled = true; };
  }, [isRsvpPage, eventOverride]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const sep = effectiveEndpoint.includes("?") ? "&" : "?";
      const url = `${API}${effectiveEndpoint}${sep}include_archived=${includeArchived}`;
      const r = await axios.get(url, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Erro a carregar registos.");
    } finally {
      setLoading(false);
    }
  }, [effectiveEndpoint, includeArchived]);

  useEffect(() => { reload(); }, [reload]);

  // When viewing all RSVPs (no event filter), prepend an "Evento" column.
  const effectiveColumns = (isRsvpPage && !eventOverride && type === "rsvps")
    ? [{
        key: "event_slug",
        label: "Evento",
        render: (v) => {
          const found = eventOptions.find((o) => o.slug === v);
          return found?.label || v || "—";
        },
      }, ...columns]
    : columns;

  const toggleArchive = async (item) => {
    if (!archiveBase) return;
    const isArch = !!item.archived;
    const action = isArch ? "unarchive" : "archive";
    if (!isArch && !window.confirm(`Arquivar "${item.full_name || item.name || item.email}"?`)) return;
    try {
      await axios.post(`${API}${archiveBase}/${item.id}/${action}`, {}, { withCredentials: true });
      toast.success(isArch ? "Reativado." : "Arquivado.");
      await reload();
    } catch (e) {
      toast.error("Erro.");
    }
  };

  const resendEmail = async (item) => {
    const sid = item.payment_session_id;
    if (!sid) return;
    if (!window.confirm(`Re-enviar email de confirmação para ${item.email}?`)) return;
    try {
      await axios.post(`${API}/admin/payments/${sid}/resend`, {}, { withCredentials: true });
      toast.success("Email re-enviado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a re-enviar.");
    }
  };

  const deleteItem = async (item) => {
    if (!archiveBase) return;
    if (item.paid && !item.archived) {
      toast.error("Inscrição paga: arquive primeiro e depois apague.");
      return;
    }
    const who = item.full_name || item.name || item.email;
    const warn = item.paid
      ? `Apagar definitivamente a inscrição PAGA de "${who}"?\n\n• A linha desaparece da tabela.\n• A Declaração de Donativo já emitida CONTINUA preservada em /admin/declarações.\n\nEsta acção é irreversível.`
      : `Apagar definitivamente a inscrição de "${who}"?\n\nEsta acção é irreversível.`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}${archiveBase}/${item.id}`, { withCredentials: true });
      toast.success("Inscrição apagada.");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a apagar.");
    }
  };

  const showResendButton = (it) => it.paid && it.payment_session_id;
  const hasActions = !!archiveBase || ["rsvps", "donations"].includes(type);

  const filtered = useMemo(() => {
    if (!q) return items;
    const lc = q.toLowerCase();
    return items.filter((it) =>
      Object.values(it).some((v) => String(v ?? "").toLowerCase().includes(lc))
    );
  }, [items, q]);

  const handleDownload = async () => {
    try {
      const r = await axios.get(`${API}${effectiveCsvUrl}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = effectiveCsvUrl.split("/").pop().split("?")[0];
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descarregado.");
    } catch (e) {
      toast.error("Erro a descarregar CSV.");
    }
  };

  return (
    <div className={embedded ? "pt-6" : "px-8 py-10 lg:px-12 lg:py-14"} data-testid={`admin-list-${type}`}>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {!embedded && (
            <>
              <div className="overline">{overline}</div>
              <h1 className="display-heading text-4xl sm:text-5xl mt-3" data-testid="admin-list-title">{effectiveTitle}</h1>
              <span className="accent-rule mt-6 block" />
            </>
          )}
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            {loading ? "…" : `${filtered.length} de ${items.length} registos`}
          </p>
          {isRsvpPage && eventOptions.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="admin-event-switcher">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Evento:</span>
              <select
                value={eventOverride || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = new URLSearchParams(searchParams);
                  if (v) next.set("event", v); else next.delete("event");
                  setSearchParams(next, { replace: true });
                }}
                className="bg-white border border-[hsl(var(--border))] px-3 py-1.5 text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
                data-testid="admin-event-select"
              >
                <option value="">— Todos os eventos —</option>
                {eventOptions.map((o) => (
                  <option key={o.slug} value={o.slug}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {archiveBase && (
            <label className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] cursor-pointer" data-testid="admin-show-archived">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="accent-[hsl(var(--primary))]"
              />
              Mostrar arquivados
            </label>
          )}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar…"
              className="pl-9 pr-3 py-2.5 bg-white border border-[hsl(var(--border))] text-sm w-64 focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="admin-search-input"
            />
          </div>
          <button
            onClick={handleDownload}
            className="btn-outline"
            data-testid="admin-download-csv"
          >
            <Download className="h-4 w-4" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="mt-10 bg-white border border-[hsl(var(--border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              {effectiveColumns.map((c) => (
                <th
                  key={c.key}
                  className="text-left text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3"
                >
                  {c.label}
                </th>
              ))}
              {hasActions && (
                <th className="text-right text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-4 py-3">Ações</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={effectiveColumns.length + (hasActions ? 1 : 0)} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">A carregar…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={effectiveColumns.length + (hasActions ? 1 : 0)} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">{defaultEmpty || "Sem registos."}</td></tr>
            ) : (
              filtered.map((it, i) => (
                <tr key={it.id || i} className={`border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--secondary))]/40 transition-colors ${it.archived ? "opacity-50" : ""}`}>
                  {effectiveColumns.map((c) => {
                    const raw = it[c.key];
                    const v = c.render ? c.render(raw, it) : (raw ?? "—");
                    return (
                      <td key={c.key} className="px-4 py-3 text-[hsl(var(--foreground))] align-top">
                        <div className="max-w-[280px] whitespace-pre-wrap break-words">{v || "—"}</div>
                      </td>
                    );
                  })}
                  {hasActions && (
                    <td className="px-4 py-3 text-right whitespace-nowrap align-top">
                      <div className="inline-flex items-center gap-3">
                        {showResendButton(it) && (
                          <button
                            onClick={() => resendEmail(it)}
                            className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                            data-testid={`admin-resend-email-${it.id}`}
                            title="Re-enviar email de confirmação"
                          >
                            <Mail className="h-3.5 w-3.5" /> Re-enviar
                          </button>
                        )}
                        {archiveBase && (
                          <button
                            onClick={() => toggleArchive(it)}
                            className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                            data-testid={`admin-archive-toggle-${it.id}`}
                            title={it.archived ? "Reativar" : "Arquivar (não vai ao evento)"}
                          >
                            {it.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                            {it.archived ? "Reativar" : "Arquivar"}
                          </button>
                        )}
                        {isRsvpPage && (!it.paid || it.archived) && (
                          <button
                            onClick={() => deleteItem(it)}
                            className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-red-700 transition-colors"
                            data-testid={`admin-delete-rsvp-${it.id}`}
                            title={it.paid ? "Apagar (recibo preservado)" : "Apagar definitivamente"}
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Apagar
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
